import { useRef, useState } from "react";
import { Upload, X, Check } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Reusable image uploader that lives next to a URL text input.
 * - Accepts JPG/PNG/WEBP/HEIC up to 15MB
 * - Uploads to /api/admin/upload and returns a stable public URL
 * - Calls onUploaded(url) with the returned URL (backend absolute URL)
 */
export default function ImageUpload({ onUploaded, testId = "image-upload" }) {
  const fileRef = useRef(null);
  const [state, setState] = useState("idle"); // idle | uploading | done | error
  const [msg, setMsg] = useState("");

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const onPick = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setState("error"); setMsg("Please choose an image file."); return;
    }
    if (file.size > 15 * 1024 * 1024) {
      setState("error"); setMsg("Image is over 15MB — please pick a smaller one."); return;
    }
    setState("uploading"); setMsg("Uploading…");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // data.url is "/api/files/…" — prefix with backend URL for a fully-qualified public link
      const publicUrl = `${backendUrl}${data.url}`;
      setState("done"); setMsg("Uploaded");
      onUploaded(publicUrl);
      setTimeout(() => { setState("idle"); setMsg(""); }, 1600);
    } catch (err) {
      const detail = err.response?.data?.detail || "Upload failed. Please try again.";
      setState("error"); setMsg(detail);
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        onChange={onFile}
        className="hidden"
        data-testid={`${testId}-input`}
      />
      <button
        type="button"
        onClick={onPick}
        disabled={state === "uploading"}
        data-testid={`${testId}-btn`}
        className={`inline-flex items-center gap-2 px-3 py-2 text-[10px] uppercase tracking-[0.2em] border transition-colors ${
          state === "error" ? "border-destructive text-destructive"
          : state === "done" ? "border-sage text-sage bg-sage/10"
          : "border-rose text-rose hover:bg-rose hover:text-ivory"
        } ${state === "uploading" ? "opacity-60 cursor-wait" : ""}`}
      >
        {state === "done" ? <Check size={12} /> : state === "error" ? <X size={12} /> : <Upload size={12} />}
        {state === "uploading" ? "Uploading…" : state === "done" ? "Uploaded" : state === "error" ? "Retry" : "Upload"}
      </button>
      {msg && state === "error" && <p className="text-xs text-destructive mt-2" data-testid={`${testId}-msg`}>{msg}</p>}
    </>
  );
}
